import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../../api/client";
import type { VerifySent } from "../../api/types";
import { PhoneVerifyPanel } from "../../components/PhoneVerifyPanel";
import { meApi } from "../../api/resources";
import { PageHeader } from "../../components/ComingSoon";
import { Button, DemoBadge, ErrorText, FullScreenLoader, StatusPill } from "../../components/ui";
import { LoadState, useLoad } from "../admin/shared";
import { EMPLOYEE_STATUS_LABEL } from "../../utils/labels";
import { ThemeSwitcher } from "../../components/ThemeSwitcher";

function Row({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div className="grid gap-1 border-b border-white/5 py-4 sm:grid-cols-[200px_1fr]">
      <dt className="label-caps text-zinc-500">{label}</dt>
      <dd data-testid={testId} className="text-white">{value}</dd>
    </div>
  );
}

const fmt = (d: string | null) => (d ? new Date(d).toLocaleString() : "—");

const SMS_LABEL: Record<string, string> = { VERIFIED: "Verified", UNVERIFIED: "Unverified", OPTED_OUT: "Opted out", CONFLICT: "Needs admin review" };

export default function MyProfile() {
  const [reload, setReload] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const loader = useCallback(() => meApi.profile(), [reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data: p, error } = useLoad(loader);
  const canVerify = p && !p.phone_verified && p.account_status === "REGISTERED_UNVERIFIED" && !!p.phone_e164;
  return (
    <section data-testid="my-profile-page" className="animate-rise">
      <PageHeader title="My profile" />
      <LoadState error={error} loading={!p} testId="profile-load-error" />
      {p && (
        <div className="max-w-3xl space-y-8">
          {!p.phone_verified && (
            <div data-testid="phone-verification-pending-banner" role="status"
              className="flex items-start gap-3 rounded-sm border border-warn/40 bg-warn/10 px-5 py-4 text-sm text-amber-100">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warn" />
              <div>
                <p className="font-semibold text-white">Phone verification pending</p>
                <p className="mt-1 text-zinc-300">Verify your mobile number to activate your account.</p>
                {canVerify && !verifying && (
                  <Button className="mt-3 h-9 px-4 text-[11px]" onClick={() => setVerifying(true)} data-testid="profile-verify-phone-button">Verify my phone</Button>
                )}
              </div>
            </div>
          )}
          {verifying && p.phone_e164 && (
            <div className="rounded-sm border border-white/10 bg-surface p-8">
              <VerifyFlow phone={p.phone_e164} onVerified={() => { setVerifying(false); toast.success("Phone verified. Your account is active."); setReload((r) => r + 1); }} />
            </div>
          )}
          <div className="rounded-sm border border-white/10 bg-surface p-8">
            <div className="flex flex-wrap items-center gap-3">
              <h2 data-testid="profile-full-name" className="font-display text-3xl font-bold uppercase tracking-tight text-white">{p.full_name}</h2>
              <DemoBadge show={p.is_demo} testId="profile-demo-badge" />
              <StatusPill status={p.account_status} label={EMPLOYEE_STATUS_LABEL[p.account_status]} />
            </div>
            <dl className="mt-6">
              <Row label="Email" value={p.email} testId="profile-email" />
              <Row label="Mobile phone" value={p.phone_e164 ?? "Not provided"} testId="profile-phone" />
              <Row label="Phone status" value={p.phone_verified ? "Verified" : "Pending verification"} testId="profile-phone-status" />
              <Row label="SMS status" value={SMS_LABEL[p.sms?.sms_status ?? "UNVERIFIED"]} testId="profile-sms-status" />
              <Row label="Text messages" value={p.sms?.sms_opted_out ? `Opted out${p.sms.sms_opted_out_at ? ` on ${fmt(p.sms.sms_opted_out_at)}` : ""} (text START to opt back in)` : "Subscribed"} testId="profile-sms-opt-out" />
              <Row label="Account status" value={EMPLOYEE_STATUS_LABEL[p.account_status] ?? p.account_status} testId="profile-account-status" />
              <Row label="Registered" value={fmt(p.registered_at)} testId="profile-registered-at" />
            </dl>
            {p.phone_verified && (
              <p data-testid="profile-phone-verified" className="mt-6 inline-flex items-center gap-2 text-sm text-emerald-400">
                <BadgeCheck className="h-4 w-4" /> Phone verified
              </p>
            )}
            <p className="mt-6 text-xs text-zinc-500">This profile is read-only. Contact your crew lead to change details.</p>
          </div>
          <ThemeSwitcher />
        </div>
      )}
    </section>
  );
}

function VerifyFlow({ phone, onVerified }: { phone: string; onVerified: () => void }) {
  const [sent, setSent] = useState<VerifySent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    meApi.verifyStart().then(setSent).catch((err) => setError(errorMessage(err)));
  }, []);
  if (!sent && !error) return <FullScreenLoader />;
  if (!sent) return <ErrorText message={error} testId="profile-verify-error" />;
  return <PhoneVerifyPanel phoneMasked={sent.phone_masked ?? `•••-•••-${phone.slice(-4)}`} sent onResend={meApi.verifyStart} onCheck={meApi.verifyCheck} onVerified={onVerified} />;
}
